import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { CheckCircle, XCircle, Clock, Loader } from 'lucide-react';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 2rem;
`;

const Card = styled.div`
  background: white;
  border-radius: 16px;
  padding: 3rem;
  max-width: 500px;
  width: 100%;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const IconWrapper = styled.div<{ $status: 'success' | 'pending' | 'failure' | 'loading' }>`
  width: 80px;
  height: 80px;
  margin: 0 auto 2rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;

  ${props => {
    switch (props.$status) {
      case 'success':
        return `background: #d4edda; color: #28a745;`;
      case 'pending':
        return `background: #fff3cd; color: #ffc107;`;
      case 'failure':
        return `background: #f8d7da; color: #dc3545;`;
      default:
        return `background: #e9ecef; color: #666;`;
    }
  }}
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 1rem;
`;

const Message = styled.p`
  font-size: 1.1rem;
  color: #666;
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const Button = styled.button`
  background: linear-gradient(135deg, #00d4aa 0%, #00b894 100%);
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button`
  background: transparent;
  color: #555;
  border: 2px solid #e1e5ea;
  padding: 1rem 2rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.2s ease, transform 0.2s ease, color 0.2s ease;

  &:hover {
    border-color: #cfd6dd;
    color: #333;
    transform: translateY(-2px);
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
`;

const InfoBox = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1rem;
  margin-top: 1.5rem;
  text-align: left;
  font-size: 0.9rem;
  color: #666;
`;

interface PaymentStatus {
  status: 'success' | 'pending' | 'failure' | 'loading';
  title: string;
  message: string;
}

export const PaymentCallbackPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>({
    status: 'loading',
    title: 'Processando...',
    message: 'Aguarde enquanto verificamos seu pagamento.'
  });

  useEffect(() => {
    const checkPaymentStatus = () => {
      const path = window.location.pathname;

      if (path.includes('/payment/success')) {
        setPaymentStatus({
          status: 'success',
          title: 'Pagamento Confirmado!',
          message: 'Seu pagamento foi processado com sucesso. Sua assinatura está ativa e você já pode utilizar todos os recursos do plano contratado.'
        });
      } else if (path.includes('/payment/pending')) {
        setPaymentStatus({
          status: 'pending',
          title: 'Pagamento Pendente',
          message: 'Seu pagamento está sendo processado. Você receberá uma notificação assim que for confirmado. Isso pode levar alguns minutos.'
        });
      } else if (path.includes('/payment/failure')) {
        setPaymentStatus({
          status: 'failure',
          title: 'Pagamento Não Aprovado',
          message: 'Não foi possível processar seu pagamento. Por favor, verifique os dados do cartão e tente novamente.'
        });
      }
    };

    checkPaymentStatus();
  }, [searchParams]);

  const handleGoToBilling = () => {
    navigate('/billing');
  };

  const handleGoToDashboard = () => {
    navigate('/dashboard');
  };

  const handleGoToPlans = () => {
    navigate('/plans');
  };

  const getIcon = () => {
    switch (paymentStatus.status) {
      case 'success':
        return <CheckCircle size={48} />;
      case 'pending':
        return <Clock size={48} />;
      case 'failure':
        return <XCircle size={48} />;
      default:
        return <Loader size={48} className="animate-spin" />;
    }
  };

  return (
    <Container>
      <Card>
        <IconWrapper $status={paymentStatus.status}>
          {getIcon()}
        </IconWrapper>

        <Title>{paymentStatus.title}</Title>
        <Message>{paymentStatus.message}</Message>

        {paymentStatus.status === 'success' && (
          <>
            <Button onClick={handleGoToDashboard}>
              Ir para o Dashboard
            </Button>
            <InfoBox>
              <strong>Próximos passos:</strong>
              <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
                <li>Acesse sua conta e comece a usar os recursos</li>
                <li>Configure suas lojas e produtos</li>
                <li>Convide sua equipe para colaborar</li>
              </ul>
            </InfoBox>
          </>
        )}

        {paymentStatus.status === 'pending' && (
          <>
            <Button onClick={handleGoToBilling}>
              Ver Detalhes da Cobrança
            </Button>
            <InfoBox>
              <strong>Sobre pagamentos pendentes:</strong>
              <p style={{ marginTop: '0.5rem' }}>
                Pagamentos podem ficar pendentes por alguns motivos, como análise de segurança ou método de pagamento escolhido (boleto, por exemplo).
                Você receberá um e-mail assim que o pagamento for confirmado.
              </p>
            </InfoBox>
          </>
        )}

        {paymentStatus.status === 'failure' && (
          <>
            <ButtonRow>
              <Button onClick={handleGoToPlans}>
                Tentar Novamente
              </Button>
              <SecondaryButton onClick={handleGoToDashboard}>
                Cancelar
              </SecondaryButton>
            </ButtonRow>
            <InfoBox>
              <strong>Possíveis causas:</strong>
              <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
                <li>Dados do cartão incorretos</li>
                <li>Saldo insuficiente</li>
                <li>Limite de crédito excedido</li>
                <li>Cartão bloqueado ou vencido</li>
              </ul>
            </InfoBox>
          </>
        )}
      </Card>
    </Container>
  );
};
